import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination,
  FilterBar, FilterField, RadixSelect, type DataTableColumn, type RowActionItem,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import {
  usePoliciesQuery,
  usePolicyMutation,
  useSetDefaultPolicyMutation,
} from '../hooks/usePoliciesQuery';
import { POLICY_TYPES, type Policy, type PolicyListQuery, type PolicyType } from '../types/policy';

const PAGE_SIZE = 20;
const ALL = '';
const ACTIVE_TRUE = 'true';
const ACTIVE_FALSE = 'false';

interface AppliedFilters {
  type: string;
  active: string;
  page: number;
}

const INITIAL_FILTERS: AppliedFilters = { type: ALL, active: ALL, page: 1 };

/** Sidebar 하위 카테고리 deep-link 용: ?type=GUIDE|TERMS|PRIVACY|EMAIL_REJECT|SHIPPING|OPERATION */
const normalizeTypeParam = (raw: string | null): string =>
  raw && (POLICY_TYPES as ReadonlyArray<string>).includes(raw) ? raw : ALL;

const formatDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('ko-KR') : '-';

/** Render the HTML content as plain text for a truncated table preview. */
const toPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const PolicyListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const typeParam = normalizeTypeParam(searchParams.get('type'));

  const [draftType, setDraftType] = useState(typeParam);
  const [draftActive, setDraftActive] = useState(ALL);
  const [applied, setApplied] = useState<AppliedFilters>({ ...INITIAL_FILTERS, type: typeParam });
  const [deleteTarget, setDeleteTarget] = useState<Policy | null>(null);

  // Sidebar 하위 카테고리 링크(?type=) 전환 시 필터 동기화.
  useEffect(() => {
    setDraftType(typeParam);
    setApplied((prev) => ({ ...prev, type: typeParam, page: 1 }));
  }, [typeParam]);

  const listQuery: PolicyListQuery = useMemo(
    () => ({
      page: applied.page,
      size: PAGE_SIZE,
      ...(applied.type ? { type: applied.type as PolicyType } : {}),
      ...(applied.active ? { active: applied.active === ACTIVE_TRUE } : {}),
    }),
    [applied],
  );

  const query = usePoliciesQuery(listQuery);

  // 서버 응답 type 가드 — 미지원/누락 값일 때 raw i18n 키(policy.type.undefined) 노출 방지 (G-all-3).
  const typeLabel = (type: string | null | undefined): string =>
    type && (POLICY_TYPES as ReadonlyArray<string>).includes(type)
      ? t(`policy.type.${type}`)
      : '-';
  const { remove } = usePolicyMutation();
  const setDefault = useSetDefaultPolicyMutation();

  const items = query.data?.items ?? [];
  const pagination = query.data?.pagination;

  const typeOptions = useMemo(
    () => [
      { value: ALL, label: t('policy.filter.typeAll') },
      ...POLICY_TYPES.map((type) => ({ value: type, label: t(`policy.type.${type}`) })),
    ],
    [t],
  );

  const activeOptions = useMemo(
    () => [
      { value: ALL, label: t('policy.filter.activeAll') },
      { value: ACTIVE_TRUE, label: t('policy.active') },
      { value: ACTIVE_FALSE, label: t('policy.inactive') },
    ],
    [t],
  );

  const applyFilters = () => setApplied({ type: draftType, active: draftActive, page: 1 });

  const resetFilters = () => {
    setDraftType(ALL);
    setDraftActive(ALL);
    setApplied(INITIAL_FILTERS);
  };

  const columns: DataTableColumn<Policy>[] = [
    {
      key: 'name',
      header: t('policy.cols.name'),
      cell: (row) => (
        <span
          className="block max-w-[280px] truncate text-sm font-medium"
          style={{ color: 'var(--content-strong)' }}
          title={row.name}
        >
          {row.name}
        </span>
      ),
    },
    {
      key: 'type',
      header: t('policy.cols.type'),
      width: 120,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {typeLabel(row.type)}
        </span>
      ),
    },
    {
      key: 'content',
      header: t('policy.cols.content'),
      cell: (row) => {
        const preview = toPlainText(row.content);
        return (
          <span
            className="block max-w-[360px] truncate text-sm"
            style={{ color: 'var(--content-muted)' }}
            title={preview}
          >
            {preview || '-'}
          </span>
        );
      },
    },
    {
      key: 'isDefault',
      header: t('policy.cols.isDefault'),
      align: 'center',
      width: 96,
      cell: (row) =>
        row.isDefault ? (
          <StatusBadge variant="info" label={t('policy.default')} />
        ) : (
          <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
            -
          </span>
        ),
    },
    {
      key: 'isActive',
      header: t('policy.cols.isActive'),
      align: 'center',
      width: 88,
      cell: (row) => (
        <StatusBadge
          variant={row.isActive ? 'success' : 'neutral'}
          label={row.isActive ? t('policy.active') : t('policy.inactive')}
        />
      ),
    },
    {
      key: 'updatedAt',
      header: t('policy.cols.updatedAt'),
      width: 180,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.updatedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => {
        const actions: RowActionItem[] = [
          { key: 'edit', label: t('policy.edit'), onClick: () => navigate(`/policies/${row.id}`) },
          ...(row.isDefault
            ? []
            : [
                {
                  key: 'setDefault',
                  label: t('policy.setDefault'),
                  onClick: () => setDefault.mutate(row.id),
                },
              ]),
          {
            key: 'delete',
            label: t('policy.delete'),
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel={t('policy.rowActionsLabel')} actions={actions} />;
      },
    },
  ];

  return (
    <PageShell
      title={t('policy.title')}
      actions={
        <Link to="/policies/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('policy.create')}
          </Button>
        </Link>
      }
    >
      <FilterBar
        className="mb-4"
        filters={
          <>
            <FilterField label={t('policy.filter.type')}>
              <RadixSelect
                value={draftType}
                onChange={setDraftType}
                options={typeOptions}
                aria-label={t('policy.filter.type')}
              />
            </FilterField>
            <FilterField label={t('policy.filter.active')}>
              <RadixSelect
                value={draftActive}
                onChange={setDraftActive}
                options={activeOptions}
                aria-label={t('policy.filter.active')}
              />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button size="sm" onClick={applyFilters}>{t('policy.filter.search')}</Button>
            <Button size="sm" variant="outline" onClick={resetFilters}>{t('policy.filter.reset')}</Button>
          </>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('policy.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('policy.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          emptyState={t('policy.empty')}
          stickyHeader
        />
      )}

      {pagination ? (
        <Pagination
          total={pagination.totalItems}
          currentPage={pagination.page}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => setApplied((prev) => ({ ...prev, page: p }))}
          className="mt-4"
        />
      ) : null}

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('policy.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('policy.deleteDesc', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('policy.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('policy.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
